import { Fragment, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import UIContext from "../../../contexts/ui/UIContext";
import AuthContext from '../../../contexts/auth/AuthContext';
import SeedTypeManagementContext from '../../../contexts/seed-type-management/SeedTypeManagementContext';

import StatusBarButton from '../../layout/StatusBarButton';
import Loading from '../../ui-elements/Loading';
import LabeledInputField from '../../layout/LabeledInputField';
import {
    applyMeasurementChange,
    areMeasurementsValid,
    commitMeasurementState,
    createMeasurementState,
    toMeasurementRequest
} from '../../../utility/seedVariantMeasurements';

/* ================= Helpers ================= */

const INT32_MAX = 2147483647;
const INT32_MAX_BIG = 2147483647n;

const clampInt32String = (raw) => {
    if (raw === '')
    {
      return raw;
    }
    if (!/^\d+$/.test(raw))
    {
      return raw;
    }
    return BigInt(raw) > INT32_MAX_BIG ? String(INT32_MAX) : raw;
  };


const Create = () => {

    const { currentContainer, currentTenant, accessibleSeedTypes, loadAndSetAccessibleSeedTypesAsync } =
        useContext(AuthContext);
    const { addSeedType } = useContext(SeedTypeManagementContext);
    const { setInfo } = useContext(UIContext);

    const navigate = useNavigate();

    // Seed Type State
    const initialSeedTypeState = {
        name: {
            value: '',
            touched: false,
            validate: (v) => v.length >= 3,
            isValid: false,
            placeholder: '',
            errorMessage: 'Name must have three or more characters.'
        },
        scientificName: {
            value: '',
            touched: false,
            validate: (v) => v.length >= 3,
            isValid: false,
            placeholder: '',
            errorMessage: 'Scientific name must have three or more characters.'
        },
        commonName: {
            value: '',
            touched: false,
            validate: (v) => v.length >= 3,
            isValid: false,
            placeholder: '',
            errorMessage: 'Common name must have three or more characters.'
        },
        number: {
            value: 0,
            touched: false,
            validate: (text) => {
                if (
                    text === '' ||
                    /^0+$/.test(text) ||
                    !/^\d+$/.test(text) ||
                    BigInt(text) > import.meta.env.VITE_MAX_SEEDTYPES_NUMBER_VALUE
                ) return false;

                const sanitized = text.toString().replace(/^0+/, '');
                return (
                    accessibleSeedTypes
                        ?.filter((s) => !s.outdated)
                        .map((s) => s.number.toString())
                        .includes(sanitized) === false
                );
            },
            isValid: false,
            placeholder: 0,
            errorMessage: 'Number is already in use or invalid.'
        },
        containerId: {
            value: currentContainer?.id ?? '',
            touched: false,
            validate: () => true,
            isValid: true,
            errorMessage: ''
        },
        seedsPerPlug: {
            value: 1,
            touched: true,
            validate: (v) => parseInt(v, 10) >= 1,
            isValid: true,
            errorMessage: 'Seeds per plug cannot be 0.'
        }
    };

    // Seed Variant Type State
    const initialSeedVariantTypeState = {
        lot: {
            value: '',
            touched: false,
            validate: (v) => v.length >= 3,
            isValid: false,
            placeholder: 'Please enter the lot number',
            errorMessage: 'Lot number must have three or more characters.'
        },
        manufacturer: {
            value: '',
            touched: false,
            validate: (v) => v.length >= 3,
            isValid: false,
            placeholder: 'Please enter the manufacturer',
            errorMessage: 'Manufacturer must have three or more characters.'
        },
        seedCount: {
            ...createMeasurementState().count,
            errorMessage: 'Seed count must be set.'
        },
        packageWeight: {
            ...createMeasurementState().packageWeight,
            errorMessage: 'Package weight must be greater than zero.'
        },
        tgw: {
            ...createMeasurementState().tgw,
            errorMessage: 'TGW must be greater than zero.'
        }
    };

    const [seedTypeState, setSeedTypeState] = useState(initialSeedTypeState);
    const [seedVariantTypeState, setSeedVariantTypeState] = useState(initialSeedVariantTypeState);
    const [localLoading, setLocalLoading] = useState(false);

    useEffect(() => {
        setInfo('Add new Seed Type');
    }, []);

    const isStateValid = Object.values(seedTypeState).every((e) => e.isValid);

    // Handlers for input changes
    const onSeedTypeChange = (event) => {
        const { name } = event.currentTarget;
        let { value } = event.currentTarget;

        if (name === 'seedsPerPlug')
        {
          value = clampInt32String(value);
        }

        setSeedTypeState((prev) => ({
          ...prev,
          [name]: {
            ...prev[name],
            value,
            touched: true,
            isValid: prev[name].validate(value),
          },
        }));
      };


    const onSeedVariantTypeChange = (event) => {
        const { name, value } = event.currentTarget;
        setSeedVariantTypeState((prev) => ({
            ...prev,
            [name]: {
                ...prev[name],
                value,
                touched: true,
                isValid: prev[name].validate(value)
            }
        }));
    };

    const updateMeasurements = (changedField, rawValue) => {
        setSeedVariantTypeState((prev) => {
            const measurements = applyMeasurementChange({
                count: prev.seedCount,
                tgw: prev.tgw,
                packageWeight: prev.packageWeight
            }, changedField, rawValue);
            return {
                ...prev,
                seedCount: { ...prev.seedCount, ...measurements.count },
                tgw: { ...prev.tgw, ...measurements.tgw },
                packageWeight: { ...prev.packageWeight, ...measurements.packageWeight }
            };
        });
    };

    const onSeedCountChange = (event) => updateMeasurements('count', event.currentTarget.value);

    const onPackageWeightChange = (event) => updateMeasurements('packageWeight', event.currentTarget.value);

    const onTGWChange = (event) => updateMeasurements('tgw', event.currentTarget.value);

    const onTGWBlur = () => {
        setSeedVariantTypeState((prev) => {
            const measurements = commitMeasurementState({
                count: prev.seedCount,
                tgw: prev.tgw,
                packageWeight: prev.packageWeight
            });
            return {
                ...prev,
                seedCount: { ...prev.seedCount, ...measurements.count },
                tgw: { ...prev.tgw, ...measurements.tgw },
                packageWeight: { ...prev.packageWeight, ...measurements.packageWeight }
            };
        });
    };

    // Handlers for creating seed type and variant
    const createSeedType = () =>
        Object.fromEntries(Object.entries(seedTypeState).map(([k, v]) => [k, v.value]));

    const createSeedVariantType = () => ({
        lot: seedVariantTypeState.lot.value,
        manufacturer: seedVariantTypeState.manufacturer.value,
        ...toMeasurementRequest({
            count: seedVariantTypeState.seedCount,
            tgw: seedVariantTypeState.tgw,
            packageWeight: seedVariantTypeState.packageWeight
        })
    });

    const handleSave = async () => {
        setLocalLoading(true);
        const result = await addSeedType(createSeedType(), createSeedVariantType());
        if (result === 201) {
            await loadAndSetAccessibleSeedTypesAsync(
                currentTenant,
                currentContainer,
                false,
                'seedVariantTypes'
            );
            navigate('/management/seed-types/list');
        }
        setLocalLoading(false);
    };

    const reset = () => {
        setSeedTypeState(initialSeedTypeState);
        setSeedVariantTypeState(initialSeedVariantTypeState);
    };

    const handleDone = () => navigate('/management/seed-types/list');

    // Conditional rendering based on loading state
    return (!seedTypeState || !seedVariantTypeState || localLoading) ? <Loading fullScreen /> : (
        <div className='seed-management-container'>
            <div className='add-new-seed-card'>
                <div className='add-seed-type-title add-seed-type'>
                    Seed Type
                </div>
                <div className='name-pos'>
                    <LabeledInputField
                        labelText='Name'
                        inputName='name'
                        value={seedTypeState?.name?.value}
                        isValid={seedTypeState?.name?.isValid}
                        touched={seedTypeState?.name?.touched}
                        inputType='text'
                        onChange={onSeedTypeChange}
                        placeholder={seedTypeState?.name?.placeholder}
                        errorMessage='Name must have at least 3 characters.'
                    />
                </div>
                <div className="scientific-name-pos">
                    <LabeledInputField
                        labelText='Scientific Name'
                        inputName='scientificName'
                        value={seedTypeState?.scientificName?.value}
                        isValid={seedTypeState?.scientificName?.isValid}
                        touched={seedTypeState?.scientificName?.touched}
                        inputType='text'
                        onChange={onSeedTypeChange}
                        placeholder={seedTypeState?.scientificName?.placeholder}
                        errorMessage='Scientific name must have at least 3 characters.'
                    />
                </div>
                <div className="common-name-pos">
                    <LabeledInputField
                        labelText='Common Name'
                        inputName='commonName'
                        value={seedTypeState?.commonName?.value}
                        isValid={seedTypeState?.commonName?.isValid}
                        touched={seedTypeState?.commonName?.touched}
                        inputType='text'
                        onChange={onSeedTypeChange}
                        placeholder={seedTypeState?.commonName?.placeholder}
                        errorMessage='Common name must have at least 3 characters.'
                    />
                </div>
                <div className="number-pos">
                    <LabeledInputField
                        labelText='Number'
                        inputName='number'
                        value={seedTypeState?.number?.value}
                        isValid={seedTypeState?.number?.isValid}
                        touched={seedTypeState?.number?.touched}
                        inputType='number'
                        step={1}
                        onChange={onSeedTypeChange}
                        placeholder={seedTypeState?.number?.placeholder}
                        errorMessage={seedTypeState?.number?.errorMessage}
                    /></div>
                <div className="seeds-per-plug-pos">
                    <LabeledInputField
                        labelText='Seeds per Plug'
                        inputName='seedsPerPlug'
                        max={INT32_MAX}
                        value={seedTypeState?.seedsPerPlug?.value}
                        isValid={seedTypeState?.seedsPerPlug?.isValid}
                        touched={seedTypeState?.seedsPerPlug?.touched}
                        inputType='number'
                        step={1}
                        onChange={onSeedTypeChange}
                        placeholder={seedTypeState?.seedsPerPlug?.placeholder}
                        errorMessage={seedTypeState?.seedsPerPlug?.errorMessage}
                    /></div>
                <div className='add-seed-type-title add-seed-type-variant'>
                    Seed Type Variant Details
                </div>
                <div className="lot-pos">
                    <LabeledInputField
                        labelText='LOT'
                        inputName='lot'
                        value={seedVariantTypeState?.lot?.value}
                        isValid={seedVariantTypeState?.lot?.isValid}
                        touched={seedVariantTypeState?.lot?.touched}
                        inputType='text'
                        onChange={onSeedVariantTypeChange}
                        placeholder={seedVariantTypeState?.lot?.placeholder}
                        errorMessage={seedVariantTypeState?.lot?.errorMessage}
                    />
                </div>
                <div className="manufacturer-pos">
                    <LabeledInputField
                        labelText='Manufacturer'
                        inputName='manufacturer'
                        value={seedVariantTypeState?.manufacturer?.value}
                        isValid={seedVariantTypeState?.manufacturer?.isValid}
                        touched={seedVariantTypeState?.manufacturer?.touched}
                        inputType='text'
                        onChange={onSeedVariantTypeChange}
                        placeholder={seedVariantTypeState?.manufacturer?.placeholder}
                        errorMessage={seedVariantTypeState?.manufacturer?.errorMessage}
                    />
                </div>
                <div className="seed-count-pos">
                    <LabeledInputField
                        labelText='Seed Count'
                        inputName='seedCount'
                        value={seedVariantTypeState?.seedCount?.value}
                        isValid={seedVariantTypeState?.seedCount?.isValid}
                        touched={seedVariantTypeState?.seedCount?.touched}
                        inputType='number'
                        step={1}
                        onChange={onSeedCountChange}
                        placeholder={seedVariantTypeState?.seedCount?.placeholder}
                        errorMessage={seedVariantTypeState?.seedCount?.errorMessage}
                    /></div>
                <div className="package-weight-pos">
                    <LabeledInputField
                        labelText='Package Weight [g]'
                        inputName='packageWeight'
                        value={seedVariantTypeState?.packageWeight?.value}
                        isValid={seedVariantTypeState?.packageWeight?.isValid}
                        touched={seedVariantTypeState?.packageWeight?.touched}
                        inputType='number'
                        step={0.01}
                        onChange={onPackageWeightChange}
                        placeholder={seedVariantTypeState?.packageWeight?.placeholder}
                        errorMessage={seedVariantTypeState?.packageWeight?.errorMessage}
                    />
                </div>

                <div className="tgw-pos">
                    <LabeledInputField
                        labelText='TGW [g]'
                        inputName='tgw'
                        value={seedVariantTypeState?.tgw?.value}
                        isValid={seedVariantTypeState?.tgw?.isValid}
                        touched={seedVariantTypeState?.tgw?.touched}
                        inputType='number'
                        step={0.01}
                        onChange={onTGWChange}
                        onBlur={onTGWBlur}
                        placeholder={seedVariantTypeState?.tgw?.placeholder}
                        errorMessage={seedVariantTypeState?.tgw?.errorMessage}
                    />
                </div>
                <div className='card-button seed-card-footer'>
                    <Fragment>
                        <StatusBarButton
                            className='seed-card-btn'
                            statusSlot={1}
                            label='Reset'
                            type='inline'
                            icon='fas fa-undo'
                            clickHandler={reset}
                        />
                        <StatusBarButton statusSlot={3} label='Abort' clickHandler={handleDone} icon='fas fa-ban' type='inline' />
                        <StatusBarButton
                            className='seed-card-btn'
                            label='Confirm'
                            statusSlot={5}
                            icon='fas fa-check'
                            type='inline'
                            reversed={true}
                            clickHandler={handleSave}
                            disabled={!isStateValid || !seedVariantTypeState.manufacturer.isValid || !seedVariantTypeState.lot.isValid || !areMeasurementsValid({ count: seedVariantTypeState.seedCount, tgw: seedVariantTypeState.tgw, packageWeight: seedVariantTypeState.packageWeight })}
                            />
                    </Fragment>
                </div>
            </div>
        </div>
    );
};

export default Create;
